<template>
  <div class="wt2-add-keywords" :class="theme">
    <ul
      class="-keywords-container"
      ref="keywords"
      :class="{ '-disabled-keywords': disabled }"
      :style="{ 'max-height': containerMaxHeight }"
    >
      <li
        v-for="keyword in keywords"
        :key="keyword"
        class="-keyword"
        :class="{ '-add-in-focus': isAddInFocus }"
      >
        <!-- fallback content here -->
        <slot name="keyword" :keyword="keyword">
          <span>{{ keyword }}</span>
          <i
            class="wticon wticon-close"
            @click="$emit('removekeyword', keyword)"
          ></i>
        </slot>
      </li>
      <li
        class="-add"
        :style="delimiters.length ? placeholderStyle : addStyle"
        ref="add"
      >
        <!-- fallback content here -->
        <slot name="add">
          <input
            type="text"
            @focusin="isAddInFocus = true"
            @focusout="isAddInFocus = false"
            ref="input"
            @blur="onBlur"
            @keydown="preventDefaultFocusOut($event)"
            @keyup="monitorInput"
            v-model="tempKeyword"
            :placeholder="placeholder"
            v-bind="$attrs"
            v-on="$listeners"
          />
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  inheritAttrs: false,

  props: {
    keywords: {
      type: Array,
      default: () => [],
    },

    emptyKeywordsPlaceholder: {
      type: String,
      default: 'Add keywords',
    },

    hasKeywordsPlaceholder: {
      type: String,
      default: 'Add',
    },

    theme: {
      type: String,
      default: 'white-theme',
    },

    keywordHeight: {
      type: Number,
      default: 31,
    },

    maxRows: {
      type: Number,
      default: 3,
    },

    /**
     * delimiters represents two types:
     *  has delimiters ie: [13]
     *    - sets input width by input text size
     *
     *  no delimiters ie: [] (empty array)
     *    - sets input width by space that left till end of container
     */
    delimiters: {
      type: Array,
      default: () => [$.ui.keyCode.ENTER, $.ui.keyCode.TAB],
    },

    removeWithBackspace: {
      type: Boolean,
      default: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tempKeyword: '',
      isAddInFocus: false,
      addStyle: { flex: 1 },
    };
  },

  watch: {
    keywords(n, o) {
      if (!this.delimiters.length) {
        if (n.length > o.length) {
          this.tempKeyword = '';
        }

        this.setAddStyleByLeftSpace();
      }
    },
  },

  computed: {
    placeholder() {
      return _.isEmpty(this.keywords)
        ? this.emptyKeywordsPlaceholder
        : this.hasKeywordsPlaceholder;
    },

    containerMaxHeight() {
      return `${this.keywordHeight * this.maxRows + 1}px`; // 1px =  padding causes unnecessary scroll
    },

    placeholderStyle() {
      const placeholderLength = this.placeholder.length;
      const tempKeywordLength = this.tempKeyword.length;
      const fontSize = this.getFontSize();

      return {
        width:
          (tempKeywordLength > placeholderLength
            ? tempKeywordLength
            : placeholderLength) *
            (fontSize / 2) +
          15 +
          'px',
      };
    },
  },

  methods: {
    preventDefaultFocusOut(e) {
      /* if its tab, then the default behavior
             is to focus out which we don't want */
      if (e.keyCode === $.ui.keyCode.TAB) {
        e.preventDefault();
      }
    },

    setAddStyleByLeftSpace() {
      // changed add width to zero, it will stick to keywords
      this.addStyle = { width: 0 };

      // wait to re render to measure correct rect positions
      this.$nextTick(() => {
        const keywords =
          this.$refs.keywords && this.$refs.keywords.getBoundingClientRect();
        const add = this.$refs.add && this.$refs.add.getBoundingClientRect();

        if (!keywords && !add) {
          return;
        }

        const addOffsetLeft = add.left - keywords.left;
        this.addStyle = {
          width: `${keywords.width - addOffsetLeft}px`,
        };
      });
    },

    getFontSize() {
      const inputStyle = this.$refs.input && getComputedStyle(this.$refs.input);
      const fontSizeMatch = inputStyle && inputStyle['font-size'].match(/\d+/g);
      return fontSizeMatch && fontSizeMatch.length ? +fontSizeMatch[0] : 14;
    },

    onBlur() {
      if (this.delimiters.length) {
        this.tempKeyword = '';
      }
    },

    monitorInput(e) {
      if (this.delimiters.length) {
        // if it's one key, we assume it's a char and not enter for example.
        if (this.delimiters.includes(e.keyCode)) {
          if (e.key.length === 1) {
            this.tempKeyword = this.tempKeyword.slice(0, -1);
          }
          this.$emit('addkeyword', this.tempKeyword);
          this.tempKeyword = '';
        }
      } else {
        this.$emit('addkeyword', this.tempKeyword);
      }

      this.$nextTick(this.scrollToBottom);
    },

    scrollToBottom() {
      this.$refs.keywords.scrollTop = this.$refs.keywords.scrollHeight;
    },
  },
};
</script>
